import { useEffect, useState } from 'react';
import { MicrositeConfigType } from 'types';
import { useEnvironment } from 'services';
import { promoIdToMicrosite } from 'config/microsites/promoIdToMicrosite';
import { fetchRemoteConfig } from 'services/remoteConfig/fetchRemoteConfig';
import { buildMicrositeConfig } from 'services/remoteConfig/buildMicrositeConfig';
import { reportError } from 'services/datadog/data-dog-rum';

// ProviderWrapper remounts on the /qualify → / redirect and fails again, so
// report each broken promoId once per page load to keep Datadog counts honest.
const reportedPromoIds = new Set<string>();

interface RemoteConfigState {
  config: MicrositeConfigType | undefined;
  loading: boolean;
  configError: string | undefined;
}

export interface RemoteConfigResult extends RemoteConfigState {
  isEligible: boolean;
}

export function useRemoteConfig(
  promoId: string | undefined
): RemoteConfigResult {
  // Only attempt remote for promoIds not present in the local mapping.
  // Mapped promoIds are served by their bundled local configs.
  const isEligible = Boolean(promoId && !promoIdToMicrosite[promoId]);

  const [state, setState] = useState<RemoteConfigState>({
    config: undefined,
    loading: isEligible,
    configError: undefined,
  });

  const env = useEnvironment();

  useEffect(() => {
    if (!isEligible || !promoId) {
      return;
    }

    let cancelled = false;
    setState({ config: undefined, loading: true, configError: undefined });

    fetchRemoteConfig(promoId, env.remoteConfigBaseUrl)
      .then((remote) => {
        if (cancelled) return;
        if (!remote) {
          setState({
            config: undefined,
            loading: false,
            configError: undefined,
          });
          return;
        }

        setState({
          config: buildMicrositeConfig(remote),
          loading: false,
          configError: undefined,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        console.error(
          `[remoteConfig] failed to build config for "${promoId}":`,
          err
        );
        if (!reportedPromoIds.has(promoId)) {
          reportedPromoIds.add(promoId);
          reportError(error, { promoId, errorType: error.name });
        }
        setState({
          config: undefined,
          loading: false,
          configError: error.message,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isEligible, promoId, env.remoteConfigBaseUrl]);

  return { ...state, isEligible };
}
